/**
 * 認証状態のストア。
 *
 * zustand は admin の依存に入っていないので、`useSyncExternalStore` で最小限を自作する。
 * 外部ストアにする理由は「ルーターの `beforeLoad`（React の外）から同期的に読みたい」から。
 * React state に閉じ込めると、ガードのたびに render を待つ羽目になる。
 */
import * as React from 'react';
import type { ImpersonationScope, OpsUser } from '../types/api.js';
import { hasPermission, type OpsPermission } from '../lib/permissions.js';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

/**
 * 稼働中の代理セッション（赤帯の材料）。
 *
 * **正はサーバの `impersonation_sessions`**。ここはその写しに過ぎず、
 * `AppLayout` が `GET /ops/impersonations` を定期的に引き直して上書きする
 * （リロードしても赤帯が消えないのはそのため）。
 */
export interface ImpersonationBannerState {
  impersonationSessionId: string;
  organizationId: string;
  organizationName: string;
  scope: ImpersonationScope;
  /** ISO 8601。この時刻でサーバ側が自動的に切れる。 */
  expiresAt: string;
}

export interface AuthState {
  status: AuthStatus;
  user: OpsUser | null;
  /** 代理ログイン中に赤帯を出すためのフラグ（ops 自身は impersonated にならない）。 */
  impersonating: ImpersonationBannerState | null;
}

const INITIAL: AuthState = { status: 'loading', user: null, impersonating: null };

let state: AuthState = INITIAL;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function setState(next: AuthState): void {
  state = next;
  emit();
}

export const authStore = {
  getState(): AuthState {
    return state;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  setUser(user: OpsUser): void {
    setState({ ...state, status: 'authenticated', user });
  },
  clear(): void {
    setState({ status: 'anonymous', user: null, impersonating: null });
  },
  setImpersonating(value: AuthState['impersonating']): void {
    setState({ ...state, impersonating: value });
  },
  isAuthenticated(): boolean {
    return state.status === 'authenticated' && state.user !== null;
  },
};

export function useAuth(): AuthState {
  return React.useSyncExternalStore(authStore.subscribe, authStore.getState, authStore.getState);
}

/** 権限で UI 要素を出し分ける。**サーバー側のガードの代わりにはならない**（見た目の話）。 */
export function usePermission(permission: OpsPermission): boolean {
  const { user } = useAuth();
  return hasPermission(user?.permissions, permission);
}
